import { createContext, useContext, type ReactNode } from 'react';
import type { FilterFieldType } from '@/components/ui/form/FilterBuilder';
// Import sub-components
import DataListHeader from './DataListHeader';
import DataListList from './DataListList';
import type { FieldValues } from 'react-hook-form';

interface DataListContextValue<T extends FieldValues> {
  filterFields: FilterFieldType<T>[];
  defaultFilters?: Record<string, any>;
  defaultSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  defaultPageSize?: number;
  title: string;
}

// Create context with a default generic type
const DataListContext = createContext<DataListContextValue<any> | null>(null);

export const useDataListContext = <T extends FieldValues>() => {
  const context = useContext(DataListContext as unknown as React.Context<DataListContextValue<T> | null>);
  if (!context) {
    throw new Error('useDataListContext must be used within DataList provider');
  }
  return context as DataListContextValue<T>;
};

interface DataListProps<T extends FieldValues> {
  children: ReactNode;
  filterFields: FilterFieldType<T>[];
  defaultFilters?: Record<string, any>;
  defaultSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  defaultPageSize?: number;
  title: string;
}

export const DataList = <T extends FieldValues>({
  children,
  filterFields,
  defaultFilters,
  defaultSort,
  defaultPageSize,
  title,
}: DataListProps<T>) => {
  return (
    <DataListContext.Provider
      value={{ filterFields, defaultFilters, defaultSort, defaultPageSize, title } as DataListContextValue<any>}
    >
      {children}
    </DataListContext.Provider>
  );
};

// Attach sub-components as static properties for dot notation access
DataList.Header = DataListHeader;
DataList.List = DataListList;

// Export as named exports for direct import
export { DataListHeader, DataListList };
